import { env } from "@/env";

type ModalType = "signin" | "signup";

interface UserPortalUrlOptions {
  modal?: ModalType;
  redirect?: string;
  basePath?: string;
  domain?: string;
}

/**
 * Generates a user portal URL with optional modal and redirect parameters
 * @param options - Configuration options for the URL
 * @param options.modal - The modal type to show (default: "signin")
 * @param options.redirect - The URL to redirect to after authentication
 * @param options.basePath - Base path prefix (defaults to env.NEXT_PUBLIC_BASE_PATH)
 * @param options.domain - Optional domain prefix for full URLs
 * @returns The generated user portal URL
 *
 * @example
 * // Basic signin URL
 * getLoginUrl() // "/user-portal?modal=signin"
 *
 * @example
 * // With redirect
 * getLoginUrl({ redirect: "/dashboard" }) // "/user-portal?modal=signin&redirect=/dashboard"
 *
 * @example
 * // With encoded redirect
 * getLoginUrl({ redirect: "/user-portal/my/bookings" }) // "/user-portal?modal=signin&redirect=%2Fuser-portal%2Fmy%2Fbookings"
 *
 * @example
 * // With domain and basePath
 * getLoginUrl({
 *   domain: "https://example.com",
 *   basePath: "/app",
 *   redirect: "/dashboard"
 * }) // "https://example.com/app/user-portal?modal=signin&redirect=/dashboard"
 */
export function getLoginUrl(options: UserPortalUrlOptions = {}): string {
  const {
    modal = "signin",
    redirect,
    basePath = env.NEXT_PUBLIC_BASE_PATH || "",
    domain,
  } = options;

  const path = `${basePath}/user-portal`;
  const params = new URLSearchParams({ modal });

  if (redirect) {
    params.set("redirect", redirect);
  }

  const queryString = params.toString();
  const url = `${path}?${queryString}`;

  return domain ? `${domain}${url}` : url;
}
